import {Box, Button, Stack, Typography, useTheme} from '@mui/material';
import React, {Fragment} from 'react';

import {Refund} from 'domain/usecases';
import {QUERY_KEYS} from 'infra/libs/react-query/queryKeys';
import {FinishWithoutCompleteFlowEnum} from 'main/analytics/gaTagsEnum';
import {EmptySearchState, SentinelLoading} from 'presentation/components';
import {useGetPaginatedItemsReactQuery} from 'presentation/pages/private/hooks/useGetPaginatedItemsReactQuery';
import {FailureAlert} from 'presentation/pages/private/refund/components';
import {RefundCardV2} from '../RefundCardV2';
import {RefundStatusDrawerV2} from '../RefundStatusDrawerV2';
import {useConsultRefundV2} from 'presentation/pages/private/refund/hooks';
import {translations} from 'presentation/translations-new';
import {useRefundStatusDrawer} from '../../../../hooks/useRefundStatusDrawer';
import {getConsultRefund} from '../../../ConsultRefund/service/getConsultRefund';
import {DEFAULT_CONSULT_REFUND_V2_FILTERS} from '../ConsultRefundFilterDrawer/filterConstants';
import {StyledGrid} from '../ListStep/styles';

const {
	emptyRefundChangeDates,
	emptyRefundClearSearch,
	emptyRefundDescription,
	emptyRefundTitle,
	foundResultsPlural,
	foundResultsSingular,
} = translations['pt-br'].privateFlow.consultRefund;

const emptyStateAreaSx = {
	width: '100%',
	flex: 1,
	minHeight: 0,
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	justifyContent: 'center',
} as const;

export function RefundCardList() {
	const {
		clearSearchParams,
		consultEndDate,
		consultInitialDate,
		consultProtocolNumber,
		filterModality,
		filterStatus,
		requestOpenDatePicker,
	} = useConsultRefundV2();
	const theme = useTheme();

	const protocolSituationParam =
		filterStatus !== DEFAULT_CONSULT_REFUND_V2_FILTERS.status
			? Number(filterStatus)
			: undefined;
	const modalityCodeParam =
		filterModality !== DEFAULT_CONSULT_REFUND_V2_FILTERS.modality
			? Number(filterModality)
			: undefined;

	const {
		hasNextPage,
		items,
		loadingItemsList,
		itemsListIsEmpty,
		itemsListHasError,
		totalItems,
		loadMore,
		refresh,
	} = useGetPaginatedItemsReactQuery<Refund>({
		queryKey: [
			QUERY_KEYS.REFUND_CONSULT,
			consultProtocolNumber,
			consultInitialDate,
			consultEndDate,
			protocolSituationParam,
			modalityCodeParam,
		],
		queryFn: ({page}) =>
			getConsultRefund({
				protocol: consultProtocolNumber,
				startDate: consultInitialDate,
				endDate: consultEndDate,
				protocolSituation: protocolSituationParam,
				modalityCode: modalityCodeParam,
				page,
				limit: 12,
			}),
		queryOptions: {
			enabled: !!consultProtocolNumber || (!!consultInitialDate && !!consultEndDate),
			refetchOnWindowFocus: false,
		},
	});

	const {
		openDrawerState,
		onOpenDrawer,
		onCloseDrawer,
		getRefundCardData,
		refundDataSate,
		footer,
		chipColor,
	} = useRefundStatusDrawer();

	const filtersActive =
		filterStatus !== DEFAULT_CONSULT_REFUND_V2_FILTERS.status ||
		filterModality !== DEFAULT_CONSULT_REFUND_V2_FILTERS.modality;

	if (itemsListHasError) {
		return (
			<StyledGrid flex={1}>
				<Stack gridColumn="1/-1" gridRow="1/-1" justifyContent="center" alignSelf="center">
					<FailureAlert refetch={refresh} />
				</Stack>
			</StyledGrid>
		);
	}

	if (itemsListIsEmpty) {
		const showSearchEmptyState =
			Boolean(consultProtocolNumber.trim()) || filtersActive;

		return (
			<Box sx={emptyStateAreaSx}>
				<EmptySearchState
					sx={{width: '100%'}}
					title={emptyRefundTitle}
					customJustifyContent="center"
					customMaxWidthCard="100%"
					noMarginImage
					description={emptyRefundDescription}
					dataTagId={FinishWithoutCompleteFlowEnum.SEM_REEMBOLSO_ENCONTRADO}>
					<Button
						variant="outlined"
						size="large"
						onClick={showSearchEmptyState ? clearSearchParams : requestOpenDatePicker}>
						{showSearchEmptyState ? emptyRefundClearSearch : emptyRefundChangeDates}
					</Button>
				</EmptySearchState>
			</Box>
		);
	}

	const displayTotal = totalItems > 0 ? String(totalItems).padStart(2, '0') : '00';
	const foundResultsLabel =
		totalItems === 1 ? foundResultsSingular : foundResultsPlural;

	const shouldRenderSentinel =
		Boolean(hasNextPage) || (loadingItemsList && items.length === 0);

	return (
		<Fragment>
			<RefundStatusDrawerV2
				title={refundDataSate?.protocol ?? ''}
				protocolCode={String(refundDataSate?.protocolCode ?? '')}
				chip={{
					...footer,
					chipColor,
				}}
				refund={refundDataSate}
				openDrawer={openDrawerState}
				onOpenDrawer={onOpenDrawer}
				onCloseDrawer={onCloseDrawer}
			/>

			{!loadingItemsList && items ? (
				<Typography color={theme.palette.customInfo.shades?.['160p']}>
					<b>{displayTotal}</b> {foundResultsLabel}
				</Typography>
			) : null}

			<StyledGrid>
				{items.map(item => (
					<RefundCardV2
						key={item.protocolCode}
						data={item}
						onViewMoreClick={getRefundCardData}
					/>
				))}
			</StyledGrid>
			{shouldRenderSentinel ? (
				<SentinelLoading
					minHeight="200px"
					onVisible={() => {
						if (items.length > 0) {
							loadMore();
						}
					}}
					isLoading={loadingItemsList}
				/>
			) : null}
		</Fragment>
	);
}
